import {
  Card,
  FlatActionList,
  Page,
  Text,
  Stack,
} from "@towersystems/roam-common-native";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import React from "react";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import Location from "../../../../utilities/data/model/Location";
import { Q } from "@nozbe/watermelondb";
import { useTranslation } from "../../../../utilities/i18n";
import { useGenericFilterListScreenProvider } from "../../../../components/GenericFilterListScreen";
import { SalesFilterValues } from "../../types";
import {
  SaleRootParamList,
  SalesListFilterRootParamList,
} from "../../../../types";
import { StackScreenProps } from "@react-navigation/stack";
import {
  SALE_SOURCE_FILTER_OPTIONS,
  SALE_TYPE_FILTER_OPTIONS,
} from "./constants";
import { Switch, View } from "react-native";

export type SalesListFilterScreenProps = StackScreenProps<
  SaleRootParamList & SalesListFilterRootParamList,
  "SalesListFilterScreen"
>;

export const SalesListFilterScreen = ({
  navigation,
}: SalesListFilterScreenProps) => {
  const { t } = useTranslation("sales");
  const context = useListFilterContext<SalesFilterValues>();

  const db = useDatabase();
  const { setScreenOptions } = useGenericFilterListScreenProvider();

  const handleOnLocationFilterSelect = React.useCallback(async () => {
    const locations = await db.collections
      .get<Location>("locations")
      .query(Q.and(Q.where("deleted_at", Q.eq(null))));

    setScreenOptions({
      title: t("action.filter_sale_location.label"),
      options: locations.map((location) => ({
        id: location.id,
        name: location.name,
      })),
      initialSelectedOptions: context.getFilterValues("locationId") ?? [],
      onSelectOption: (ids) => context.setFilterValue("locationId", ids),
    });

    navigation.navigate("GenericFilterListScreen");
  }, []);

  const handleOnTerminalFilterSelect = React.useCallback(async () => {
    const terminals = await db.collections
      .get<Location>("terminals")
      .query(Q.and(Q.where("deleted_at", Q.eq(null))));

    setScreenOptions({
      title: t("action.filter_sales_terminal.label"),
      options: terminals.map((terminal) => ({
        id: terminal.id,
        name: terminal.name,
      })),
      initialSelectedOptions: context.getFilterValues("terminalId") ?? [],
      onSelectOption: (ids) => context.setFilterValue("terminalId", ids),
    });

    navigation.navigate("GenericFilterListScreen");
  }, []);

  const handleOnSaleSourceFilterSelect = React.useCallback(() => {
    setScreenOptions({
      title: t("action.filter_sales_source.label"),
      options: SALE_SOURCE_FILTER_OPTIONS,
      initialSelectedOptions: context.getFilterValues("source") ?? [],
      onSelectOption: (ids) => context.setFilterValue("source", ids),
    });

    navigation.navigate("GenericFilterListScreen");
  }, []);

  const handleOnSaleTypeFilterSelect = React.useCallback(() => {
    setScreenOptions({
      title: t("action.filter_sales_type.label"),
      options: SALE_TYPE_FILTER_OPTIONS,
      initialSelectedOptions: context.getFilterValues("saleType") ?? [],
      onSelectOption: (ids) => context.setFilterValue("saleType", ids),
    });

    navigation.navigate("GenericFilterListScreen");
  }, []);

  const watchedLocation = context.watch("locationId");
  const watchedSaleType = context.watch("saleType", []);
  const watchedProductIds = context.watch("productId", []);
  const watchedCustomerIds = context.watch("customerId", []);
  const watchedTerminalIds = context.watch("terminalId", []);
  const watchedIsAcceptedQuote = context.watch("isAcceptedQuote", false);
  const watchedIsReturned = context.watch("isReturn", false);
  const watchedSource = context.watch("source", []);

  return (
    <Page
      title={t("select_sales_filters_title")}
      secondaryTitle={t("select_sales_filters_subtitle")}
    >
      <Stack vertical>
        <Card>
          <FlatActionList
            items={[
              {
                key: "locationId",
                title: t("action.filter_sale_location.label"),
                subTitle: t("action.filter_sale_location.description"),
                onPress: handleOnLocationFilterSelect,
                isAction: (watchedLocation?.length || 0) > 0,
                componentRight: (icon) => {
                  return (watchedLocation?.length || 0) > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">
                        {watchedLocation?.length} selected
                      </Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },
              {
                key: "terminalIds",
                title: t("action.filter_sales_terminal.label"),
                subTitle: t("action.filter_sales_terminal.description"),
                onPress: handleOnTerminalFilterSelect,
                isAction: (watchedTerminalIds?.length || 0) > 0,
                componentRight: (icon) => {
                  return (watchedTerminalIds?.length || 0) > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">
                        {watchedTerminalIds?.length} selected
                      </Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },
              {
                key: "saleType",
                title: t("action.filter_sales_type.label"),
                subTitle: t("action.filter_sales_type.description"),
                onPress: handleOnSaleTypeFilterSelect,
                isAction: (watchedSaleType?.length || 0) > 0,
                componentRight: (icon) => {
                  return (watchedSaleType?.length || 0) > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">
                        {watchedSaleType?.length} selected
                      </Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },

              {
                key: "watchedSource",
                title: t("action.filter_sales_source.label"),
                subTitle: t("action.filter_sales_source.description"),
                onPress: handleOnSaleSourceFilterSelect,
                isAction: (watchedSource?.length || 0) > 0,
                componentRight: (icon) => {
                  return (watchedSource?.length || 0) > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">
                        {watchedSource?.length} selected
                      </Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },

              {
                key: "productId",
                title: t("action.filter_sales_product.label"),
                subTitle: t("action.filter_sales_product.description"),
                onPress: () =>
                  navigation.navigate("SelectableProductsFilterScreen"),
                isAction: (watchedProductIds?.length || 0) > 0,
                componentRight: (icon) => {
                  return (watchedProductIds?.length || 0) > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">
                        {watchedProductIds?.length} selected
                      </Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },
              {
                key: "customerId",
                title: t("action.filter_sales_customer.label"),
                subTitle: t("action.filter_sales_customer.description"),
                onPress: () =>
                  navigation.navigate("SelectableCustomersFilterScreen"),
                isAction: (watchedCustomerIds?.length || 0) > 0,
                componentRight: (icon) => {
                  return (watchedCustomerIds?.length || 0) > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">
                        {watchedCustomerIds?.length} selected
                      </Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },
            ]}
          />
        </Card>
        <Card>
          <FlatActionList
            items={[
              {
                key: "filter_sales_accepted_quote",
                title: t("action.filter_sales_accepted_quote.label"),
                subTitle: t("action.filter_sales_accepted_quote.description"),
                isAction: watchedIsAcceptedQuote,
                componentRight: (icon) => {
                  return (
                    <View>
                      <Switch
                        value={watchedIsAcceptedQuote}
                        onValueChange={() => {
                          context.setFilterValue(
                            "isAcceptedQuote",
                            !watchedIsAcceptedQuote
                          );
                        }}
                      />
                    </View>
                  );
                },
              },

              {
                key: "filter_sales_return",
                title: t("action.filter_sales_return.label"),
                subTitle: t("action.filter_sales_return.description"),
                isAction: watchedIsReturned,
                componentRight: (icon) => {
                  return (
                    <View>
                      <Switch
                        value={watchedIsReturned}
                        onValueChange={() => {
                          context.setFilterValue(
                            "isReturn",
                            !watchedIsReturned
                          );
                        }}
                      />
                    </View>
                  );
                },
              },
            ]}
          />
        </Card>
      </Stack>
    </Page>
  );
};
